import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

export default function VendorPlanSelection() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState("monthly");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  // New state for enhancements
  const [switchType, setSwitchType] = useState("immediate"); // "immediate" | "scheduled"
  const [scheduledDate, setScheduledDate] = useState("");
  const [proration, setProration] = useState(null); // { amount, type, daysRemaining, daysTotal }
  const [changeHistory, setChangeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [razorpayReady, setRazorpayReady] = useState(false);

  useEffect(() => {
    fetchData();
    // Preload Razorpay checkout script
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => setRazorpayReady(true);
      document.body.appendChild(script);
    } else {
      setRazorpayReady(true);
    }
  }, [user]);

  // Recalculate proration whenever plan or cycle changes
  useEffect(() => {
    if (selectedPlan && currentSubscription?.plan_id && currentSubscription?.payment_status === "active") {
      calculateProration();
    } else {
      setProration(null);
    }
  }, [selectedPlan, selectedCycle, currentSubscription]);

  const fetchData = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const [plansRes, subRes, invoicesRes] = await Promise.allSettled([
        api.get('/super-admin/plans'),
        api.get('/super-admin/my-subscription'),
        api.get('/super-admin/my-invoices'),
      ]);

      const activePlans = (plansRes.status === 'fulfilled' ? plansRes.value?.data || [] : [])
        .filter(p => p.is_active && !p.is_trial);
      setPlans(activePlans);

      if (subRes.status === 'fulfilled') {
        const sub = subRes.value?.data;
        setCurrentSubscription(sub);
        if (sub?.payment_status === 'trial') setTrialInfo(sub);
      }

      if (invoicesRes.status === 'fulfilled') {
        const invoices = invoicesRes.value?.data || [];
        const history = invoices.map((inv) => ({
          id: inv.id,
          to_plan_name: inv.plan_name,
          billing_cycle: inv.billing_cycle,
          switch_type: 'immediate',
          scheduled_date: null,
          proration_type: 'none',
          proration_amount: 0,
          changed_at: inv.paid_at || inv.created_at,
        }));
        setChangeHistory(history);
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setLoading(false);
    }
  };

  const calculateProration = () => {
    if (!currentSubscription?.start_date || !currentSubscription?.end_date) {
      setProration(null);
      return;
    }

    const now = new Date();
    const startDate = new Date(currentSubscription?.start_date);
    const endDate = new Date(currentSubscription?.end_date);
    const totalDays = Math.max(1, Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)));
    const daysRemaining = Math.max(0, Math.round((endDate - now) / (1000 * 60 * 60 * 24)));
    const daysUsed = totalDays - daysRemaining;

    const currentPlan = plans?.find((p) => p?.id === currentSubscription?.plan_id);
    const newPlan = plans?.find((p) => p?.id === selectedPlan);

    if (!currentPlan || !newPlan) {
      setProration(null);
      return;
    }

    const currentPrice =
      currentSubscription?.billing_cycle === "monthly"
        ? currentPlan?.price_monthly
        : currentPlan?.price_yearly;
    const newPrice =
      selectedCycle === "monthly" ? newPlan?.price_monthly : newPlan?.price_yearly;

    const dailyCurrentRate = currentPrice / totalDays;
    const dailyNewRate = newPrice / (selectedCycle === "monthly" ? 30 : 365);

    const unusedCredit = dailyCurrentRate * daysRemaining;
    const newCharge = dailyNewRate * daysRemaining;
    const netAmount = newCharge - unusedCredit;

    setProration({
      amount: Math.abs(netAmount),
      type: netAmount > 0.5 ? "charge" : netAmount < -0.5 ? "credit" : "none",
      daysRemaining,
      daysTotal: totalDays,
      daysUsed,
      unusedCredit,
      newCharge,
      currentPlanName: currentPlan?.plan_name,
      newPlanName: newPlan?.plan_name,
    });
  };

  // Free plan switch — used only when vendor already has an active subscription
  const handleSelectPlan = async () => {
    if (!selectedPlan) return setError("Please select a plan to continue.");
    if (switchType === "scheduled" && !scheduledDate) return setError("Please select a scheduled switch date.");
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await api.post('/super-admin/select-plan', {
        plan_id: selectedPlan,
        billing_cycle: selectedCycle,
      });
      setSuccessMsg("Plan activated. Redirecting...");
      setTimeout(() => navigate("/gym-owner-portal"), 1500);
    } catch (err) {
      console.error("Plan selection error:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to activate plan. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Razorpay payment — used when vendor has no active subscription (trial / expired)
  const handlePayWithRazorpay = async () => {
    if (!selectedPlan) return setError("Please select a plan to continue.");
    if (!razorpayReady) return setError("Payment gateway is loading. Please try again in a moment.");

    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const { data } = await api.post('/super-admin/create-payment-order', {
        plan_id: selectedPlan,
        billing_cycle: selectedCycle,
      });

      const options = {
        key:         data.key_id,
        amount:      data.amount,
        currency:    data.currency,
        name:        'GymSaaS',
        description: `${data.plan_name} · ${selectedCycle === 'yearly' ? 'Annual' : 'Monthly'} Plan`,
        order_id:    data.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await api.post('/super-admin/verify-payment', {
              razorpay_order_id:  response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              plan_id:             selectedPlan,
              billing_cycle:       selectedCycle,
            });
            setSuccessMsg(`Payment successful! ${verifyRes.data?.plan_name} plan is now active. Redirecting...`);
            setTimeout(() => navigate("/gym-owner-portal"), 2000);
          } catch (err) {
            setError(err?.response?.data?.message || "Payment verified by Razorpay but activation failed. Contact support.");
            setSubmitting(false);
          }
        },
        prefill: {
          name:  user?.full_name || '',
          email: user?.email     || '',
        },
        theme: { color: '#6366f1' },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setError(response?.error?.description || "Payment failed. Please try again.");
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to initiate payment. Please try again.");
      setSubmitting(false);
    }
  };

  // Decides which activation path to use
  const needsPayment = currentSubscription?.payment_status !== 'active';
  const handleActivate = () => {
    if (needsPayment) return handlePayWithRazorpay();
    return handleSelectPlan();
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/login-page");
  };

  const formatLimit = (val) => (val === -1 ? "Unlimited" : val?.toLocaleString());

  const trialExpiredDays = trialInfo?.end_date
    ? Math.abs(Math.floor((new Date() - new Date(trialInfo?.end_date)) / (1000 * 60 * 60 * 24)))
    : null;

  const trialDaysRemaining = trialInfo?.end_date
    ? Math.ceil((new Date(trialInfo.end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const isTrialActive = trialDaysRemaining !== null && trialDaysRemaining > 0;

  const isCurrentPlanSelected =
    currentSubscription?.plan_id === selectedPlan &&
    currentSubscription?.billing_cycle === selectedCycle &&
    currentSubscription?.payment_status === "active";

  const minScheduledDate = (() => {
    const d = new Date();
    d?.setDate(d?.getDate() + 1);
    return d?.toISOString()?.split("T")?.[0];
  })();

  const maxScheduledDate = currentSubscription?.end_date || (() => {
    const d = new Date();
    d?.setMonth(d?.getMonth() + 1);
    return d?.toISOString()?.split("T")?.[0];
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[var(--color-muted-foreground)]">
          <Icon name="Loader2" size={20} color="currentColor" className="animate-spin" />
          <span className="text-sm font-[var(--font-body)]">Loading plans...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Top bar */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-card)]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-primary)]/15 flex items-center justify-center">
              <Icon name="Dumbbell" size={16} color="var(--color-primary)" />
            </div>
            <span className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              GymVendor
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
            >
              <Icon name="History" size={13} color="currentColor" />
              History {changeHistory?.length > 0 && `(${changeHistory?.length})`}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
            >
              <Icon name="LogOut" size={13} color="currentColor" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
            currentSubscription?.payment_status === "active"
              ? "bg-[var(--color-primary)]/15"
              : isTrialActive
              ? "bg-[var(--color-success)]/15"
              : "bg-[var(--color-warning)]/15"
          }`}>
            <Icon
              name={currentSubscription?.payment_status === "active" ? "RefreshCw" : isTrialActive ? "Clock" : "Clock"}
              size={26}
              color={
                currentSubscription?.payment_status === "active"
                  ? "var(--color-primary)"
                  : isTrialActive
                  ? "var(--color-success)"
                  : "var(--color-warning)"
              }
            />
          </div>
          <h1 className="text-2xl font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-2">
            {currentSubscription?.payment_status === "active"
              ? "Change Your Plan"
              : isTrialActive
              ? "Choose Your Plan"
              : "Your Trial Has Expired"}
          </h1>
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-body)] max-w-md mx-auto">
            {currentSubscription?.payment_status === "active"
              ? `Currently on ${currentSubscription?.subscription_plans?.plan_name || "a plan"}. Select a new plan to switch.`
              : isTrialActive
              ? `You have ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} left in your free trial. Choose a plan to continue after it ends.`
              : trialExpiredDays !== null
              ? `Your 14-day free trial ended ${trialExpiredDays} day${trialExpiredDays !== 1 ? "s" : ""} ago. Choose a plan below to continue using the Gym Owner Portal.`
              : "Your free trial period has ended. Choose a plan below to continue using the Gym Owner Portal."}
          </p>
        </div>

        {/* Plan Change History Panel */}
        {showHistory && (
          <div className="mb-8 bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--color-border)] bg-[var(--color-muted)]/40">
              <div className="flex items-center gap-2">
                <Icon name="History" size={15} color="var(--color-primary)" />
                <span className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                  Plan Change History
                </span>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
              >
                <Icon name="X" size={14} color="currentColor" />
              </button>
            </div>
            {changeHistory?.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <Icon name="ClipboardList" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  No plan changes recorded yet.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--color-border)]">
                {changeHistory?.map((entry) => (
                  <div key={entry?.id} className="px-5 py-3 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        entry?.switch_type === "scheduled" ?"bg-[var(--color-warning)]/15" :"bg-[var(--color-success)]/15"
                      }`}>
                        <Icon
                          name={entry?.switch_type === "scheduled" ? "CalendarClock" : "Zap"}
                          size={13}
                          color={entry?.switch_type === "scheduled" ? "var(--color-warning)" : "var(--color-success)"}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                            {entry?.from_plan_name || "Trial"}
                          </span>
                          <Icon name="ArrowRight" size={11} color="var(--color-muted-foreground)" />
                          <span className="text-xs font-semibold text-[var(--color-foreground)] font-[var(--font-body)]">
                            {entry?.to_plan_name || entry?.to_plan?.plan_name}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                            entry?.billing_cycle === "yearly" ?"bg-[var(--color-primary)]/10 text-[var(--color-primary)]" :"bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                          }`}>
                            {entry?.billing_cycle}
                          </span>
                        </div>
                        <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                          {entry?.switch_type === "scheduled" && entry?.scheduled_date
                            ? `Scheduled for ${new Date(entry?.scheduled_date)?.toLocaleDateString()}`
                            : `Applied immediately`}
                          {entry?.proration_type !== "none" && entry?.proration_amount > 0 && (
                            <span className={`ml-2 ${entry?.proration_type === "charge" ? "text-[var(--color-error)]" : "text-[var(--color-success)]"}`}>
                              {entry?.proration_type === "charge" ? "+" : "-"}${entry?.proration_amount?.toFixed(2)} proration
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] flex-shrink-0 mt-1">
                      {new Date(entry?.changed_at)?.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Scheduled switch notice */}
        {currentSubscription?.scheduled_switch_date && currentSubscription?.pending_plan_id && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/25 rounded-xl">
            <Icon name="CalendarClock" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-[var(--color-foreground)] font-[var(--font-body)]">
                Scheduled Plan Switch
              </p>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                Your plan will switch to{" "}
                <strong>{plans?.find((p) => p?.id === currentSubscription?.pending_plan_id)?.plan_name}</strong> on{" "}
                {new Date(currentSubscription?.scheduled_switch_date)?.toLocaleDateString()}.
              </p>
            </div>
          </div>
        )}

        {/* Billing cycle toggle */}
        <div className="flex items-center justify-center gap-1 mb-8">
          <div className="inline-flex items-center bg-[var(--color-muted)] rounded-lg p-1 gap-1">
            <button
              onClick={() => setSelectedCycle("monthly")}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${
                selectedCycle === "monthly" ?"bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm" :"text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedCycle("yearly")}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
                selectedCycle === "yearly" ?"bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm" :"text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              Yearly
              <span className="px-1.5 py-0.5 bg-[var(--color-success)]/15 text-[var(--color-success)] text-[10px] font-semibold rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Plans grid */}
        {plans?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="PackageX" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-body)]">
              No plans available. Please contact support.
            </p>
          </div>
        ) : (
          <div
            className={`grid gap-4 ${
              plans?.length === 1
                ? "max-w-sm mx-auto"
                : plans?.length === 2
                ? "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto" :"grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {plans?.map((plan, idx) => {
              const isSelected = selectedPlan === plan?.id;
              const isPopular = idx === 1 && plans?.length >= 3;
              const isCurrent =
                currentSubscription?.plan_id === plan?.id &&
                currentSubscription?.payment_status === "active";
              const price =
                selectedCycle === "monthly" ? plan?.price_monthly : plan?.price_yearly;
              const features = Array.isArray(plan?.features) ? plan?.features : [];

              return (
                <div
                  key={plan?.id}
                  onClick={() => setSelectedPlan(plan?.id)}
                  className={`relative cursor-pointer rounded-xl border-2 p-5 transition-all ${
                    isSelected
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 shadow-md"
                      : "border-[var(--color-border)] bg-[var(--color-card)] hover:border-[var(--color-primary)]/40"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-[var(--color-primary)] text-white text-[10px] font-bold rounded-full uppercase tracking-wide">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {isCurrent && (
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-0.5 bg-[var(--color-success)]/15 text-[var(--color-success)] text-[10px] font-semibold rounded-full">
                        Current
                      </span>
                    </div>
                  )}

                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <div className="w-5 h-5 rounded-full bg-[var(--color-primary)] flex items-center justify-center">
                        <Icon name="Check" size={11} color="white" />
                      </div>
                    </div>
                  )}

                  <h3 className={`text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-1 ${isCurrent ? "mt-4" : ""}`}>
                    {plan?.plan_name}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-2xl font-bold font-[var(--font-data)] text-[var(--color-foreground)]">
                      ${parseFloat(price || 0).toFixed(0)}
                    </span>
                    <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      /{selectedCycle === "monthly" ? "mo" : "yr"}
                    </span>
                  </div>

                  {/* Limits */}
                  <div className="space-y-1.5 mb-4">
                    {[
                      { icon: "GitBranch", label: "Branches", val: plan?.max_branches },
                      { icon: "Users", label: "Members", val: plan?.max_members },
                      { icon: "UserCheck", label: "Staff", val: plan?.max_staff },
                      { icon: "Dumbbell", label: "Trainers", val: plan?.max_trainers },
                    ]?.map((item) => (
                      <div key={item?.label} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 text-[var(--color-muted-foreground)]">
                          <Icon name={item?.icon} size={11} color="currentColor" />
                          <span className="font-[var(--font-caption)]">{item?.label}</span>
                        </div>
                        <span
                          className={`font-semibold font-[var(--font-data)] ${
                            item?.val === -1
                              ? "text-[var(--color-success)]"
                              : "text-[var(--color-foreground)]"
                          }`}
                        >
                          {formatLimit(item?.val)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Features */}
                  {features?.length > 0 && (
                    <div className="space-y-1 pt-3 border-t border-[var(--color-border)]">
                      {features?.slice(0, 4)?.map((f, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]"
                        >
                          <Icon name="CheckCircle2" size={11} color="var(--color-success)" />
                          <span className="font-[var(--font-caption)]">{f}</span>
                        </div>
                      ))}
                      {features?.length > 4 && (
                        <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] pl-4">
                          +{features?.length - 4} more features
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Proration Calculator */}
        {proration && selectedPlan && !isCurrentPlanSelected && (
          <div className="mt-6 max-w-lg mx-auto bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-muted)]/40">
              <Icon name="Calculator" size={14} color="var(--color-primary)" />
              <span className="text-xs font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                Proration Estimate
              </span>
              <span className="ml-auto text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Mid-cycle switch calculation
              </span>
            </div>
            <div className="px-4 py-4 space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  Days remaining in current cycle
                </span>
                <span className="font-semibold font-[var(--font-data)] text-[var(--color-foreground)]">
                  {proration?.daysRemaining} / {proration?.daysTotal} days
                </span>
              </div>
              <div className="w-full bg-[var(--color-muted)] rounded-full h-1.5">
                <div
                  className="bg-[var(--color-primary)] h-1.5 rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, (proration?.daysUsed / proration?.daysTotal) * 100)}%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs pt-1">
                <span className="text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  Unused credit ({proration?.currentPlanName})
                </span>
                <span className="font-semibold font-[var(--font-data)] text-[var(--color-success)]">
                  -${proration?.unusedCredit?.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  New plan charge ({proration?.newPlanName})
                </span>
                <span className="font-semibold font-[var(--font-data)] text-[var(--color-foreground)]">
                  +${proration?.newCharge?.toFixed(2)}
                </span>
              </div>
              <div className="pt-2 border-t border-[var(--color-border)] flex items-center justify-between">
                <span className="text-xs font-semibold font-[var(--font-body)] text-[var(--color-foreground)]">
                  {proration?.type === "charge" ?"Amount due today"
                    : proration?.type === "credit" ?"Credit to your account" :"No charge / credit"}
                </span>
                <span
                  className={`text-sm font-bold font-[var(--font-data)] ${
                    proration?.type === "charge" ?"text-[var(--color-error)]"
                      : proration?.type === "credit" ?"text-[var(--color-success)]" :"text-[var(--color-muted-foreground)]"
                  }`}
                >
                  {proration?.type === "none" ?"$0.00"
                    : `${proration?.type === "charge" ? "+" : "-"}$${proration?.amount?.toFixed(2)}`}
                </span>
              </div>
              <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                * Estimate only. Actual charges may vary based on payment processor.
              </p>
            </div>
          </div>
        )}

        {/* Switch Type Options */}
        {selectedPlan && !isCurrentPlanSelected && (
          <div className="mt-5 max-w-lg mx-auto">
            <p className="text-xs font-semibold font-[var(--font-body)] text-[var(--color-foreground)] mb-3 text-center">
              When would you like to switch?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSwitchType("immediate")}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                  switchType === "immediate" ?"border-[var(--color-primary)] bg-[var(--color-primary)]/5" :"border-[var(--color-border)] bg-[var(--color-card)] hover:border-[var(--color-primary)]/40"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  switchType === "immediate" ? "bg-[var(--color-primary)]/15" : "bg-[var(--color-muted)]"
                }`}>
                  <Icon
                    name="Zap"
                    size={16}
                    color={switchType === "immediate" ? "var(--color-primary)" : "var(--color-muted-foreground)"}
                  />
                </div>
                <div className="text-center">
                  <p className={`text-xs font-semibold font-[var(--font-body)] ${
                    switchType === "immediate" ? "text-[var(--color-primary)]" : "text-[var(--color-foreground)]"
                  }`}>
                    Switch Now
                  </p>
                  <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                    Activate immediately
                  </p>
                </div>
              </button>

              <button
                onClick={() => setSwitchType("scheduled")}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                  switchType === "scheduled" ?"border-[var(--color-warning)] bg-[var(--color-warning)]/5" :"border-[var(--color-border)] bg-[var(--color-card)] hover:border-[var(--color-warning)]/40"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  switchType === "scheduled" ? "bg-[var(--color-warning)]/15" : "bg-[var(--color-muted)]"
                }`}>
                  <Icon
                    name="CalendarClock"
                    size={16}
                    color={switchType === "scheduled" ? "var(--color-warning)" : "var(--color-muted-foreground)"}
                  />
                </div>
                <div className="text-center">
                  <p className={`text-xs font-semibold font-[var(--font-body)] ${
                    switchType === "scheduled" ? "text-[var(--color-warning)]" : "text-[var(--color-foreground)]"
                  }`}>
                    At Renewal
                  </p>
                  <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                    Switch on a future date
                  </p>
                </div>
              </button>
            </div>

            {/* Scheduled date picker */}
            {switchType === "scheduled" && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-body)] mb-1.5">
                  Select switch date
                </label>
                <input
                  type="date"
                  value={scheduledDate}
                  min={minScheduledDate}
                  max={maxScheduledDate}
                  onChange={(e) => setScheduledDate(e?.target?.value)}
                  className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-lg bg-[var(--color-card)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] font-[var(--font-body)]"
                />
                {currentSubscription?.end_date && (
                  <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                    Current cycle ends on{" "}
                    {new Date(currentSubscription?.end_date)?.toLocaleDateString()}.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-lg max-w-md mx-auto">
            <Icon name="AlertCircle" size={14} color="var(--color-error)" />
            <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
          </div>
        )}

        {/* Success */}
        {successMsg && (
          <div className="mt-4 flex items-center gap-2 p-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-lg max-w-md mx-auto">
            <Icon name="CheckCircle2" size={14} color="var(--color-success)" />
            <p className="text-xs text-[var(--color-success)] font-[var(--font-caption)]">{successMsg}</p>
          </div>
        )}

        {/* CTA */}
        {(() => {
          const selPlan  = plans?.find(p => p?.id === selectedPlan);
          const selPrice = selPlan
            ? parseFloat(selectedCycle === 'yearly' ? selPlan?.price_yearly : selPlan?.price_monthly)
            : 0;
          const showPayButton = needsPayment && switchType === "immediate";

          return (
            <div className="mt-8 flex flex-col items-center gap-3">
              {currentSubscription?.payment_status === "active" && (
                <button
                  onClick={() => navigate("/gym-owner-portal")}
                  disabled={submitting}
                  className="flex items-center gap-2 px-8 py-3 text-sm font-semibold text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-xl hover:bg-[var(--color-muted)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="X" size={16} color="currentColor" />
                  Cancel
                </button>
              )}
              <button
                onClick={handleActivate}
                disabled={!selectedPlan || submitting || isCurrentPlanSelected}
                className="flex items-center gap-2 px-8 py-3 text-sm font-semibold bg-[var(--color-primary)] text-white rounded-xl hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {submitting ? (
                  <>
                    <Icon name="Loader2" size={16} color="white" className="animate-spin" />
                    {showPayButton ? "Opening payment..." : switchType === "scheduled" ? "Scheduling..." : "Activating..."}
                  </>
                ) : isCurrentPlanSelected ? (
                  <>
                    <Icon name="CheckCircle2" size={16} color="white" />
                    Current Plan Active
                  </>
                ) : switchType === "scheduled" ? (
                  <>
                    <Icon name="CalendarClock" size={16} color="white" />
                    Schedule Plan Switch
                  </>
                ) : showPayButton ? (
                  <>
                    <Icon name="CreditCard" size={16} color="white" />
                    Pay ₹{selPrice > 0 ? selPrice.toFixed(0) : "—"} &amp; Activate
                  </>
                ) : (
                  <>
                    <Icon name="Zap" size={16} color="white" />
                    Activate Selected Plan
                  </>
                )}
              </button>
              {showPayButton && selectedPlan && !isCurrentPlanSelected && (
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  Secure payment via Razorpay · {selectedCycle === 'yearly' ? 'Billed annually' : 'Billed monthly'}
                </p>
              )}
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Contact support if you need help choosing a plan.
              </p>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
